import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { bool, func, object, oneOf, shape } from 'prop-types';
import { compose } from 'redux';
import { connect, useSelector } from 'react-redux';
import { withRouter, Redirect, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import classNames from 'classnames';
import { isEmpty } from 'lodash';

// Helper functions
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString, pathByRouteName } from '../../util/routes';
import { getDefaultLocale, isLocaleGreek, switchAppLanguage } from '../../util/genericHelpers';
import { getUrlForLocale } from '../../helpers/convert';
import { apiBaseUrl } from '../../util/api';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { ensureCurrentUser } from '../../util/data';
import {
  isSignupEmailTakenError,
  isTooManyEmailVerificationRequestsError,
} from '../../util/errors';
import { withViewport } from '../../util/uiHelpers';
import { isIndividualUser } from '../../util/dataExtractors';
import { USER_TYPE } from '../../helpers/enums';
import { StatusCode } from '../../util/enums';

// Redux
import {
  login,
  authenticationInProgress,
  signup,
  signupWithIdp,
  createContactInSendgrid,
} from '../../ducks/auth.duck';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';
import { sendVerificationEmail } from '../../ducks/user.duck';
import { verifyCodeSelector } from '../../ducks/global.duck';
import {
  buildVoucherFields,
  deriveVoucherFields,
  logVoucherDebug,
  parseVoucherParams,
  readVoucherStorage,
  resolveVoucherInput,
  writeVoucherStorage,
} from '../../util/voucherHelpers';

// Components
import {
  Page,
  Heading,
  NamedRedirect,
  SocialLoginButton,
  Footer,
  Modal,
  LayoutSingleColumn,
} from '../../components';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import TermsAndConditions from './TermsAndConditions/TermsAndConditions';
import ConfirmSignupForm from './ConfirmSignupForm/ConfirmSignupForm';
import LoginForm from './LoginForm/LoginForm';
import SignupForm from './SignupForm/SignupForm';
import EmailVerificationInfo from './EmailVerificationInfo';
import TermsOfServicePage from '../TermsOfServicePage/TermsOfServicePage';
import PrivacyPolicyPage from '../../containers/PrivacyPolicyPage/PrivacyPolicyPage';
import { FacebookLogo, GoogleLogo } from './socialLoginLogos';
import Icons from '../../components/Icons/Icons';

// Styles
import css from './AuthenticationPage.module.css';

// Images
import modalImg from '../../assets/best-talent-img5.png';
import routeConfiguration from '../../routing/routeConfiguration';
import { markPendingWebAuth } from '../../util/analyticsTracking';
import { sanitizeInternalReturnPath } from '../../util/mobileWebAuth';

const authEnglishContent = {
  [USER_TYPE.INDIVIDUAL]: <></>,
  [USER_TYPE.DOCTOR]: <>Become a Vivian Lab healthcare provider</>,
};

const authGreekContent = {
  [USER_TYPE.INDIVIDUAL]: <></>,
  [USER_TYPE.DOCTOR]: <>Γίνε επαγγελματίας υγείας στο Vivian Lab</>,
};

export const transformUserType = params => {
  // Check if userType or type is 'company'
  if (params.userType === 'company' || params.type === 'company') {
    return {
      ...params,
      userType: 'doctor',
      type: 'doctor',
    };
  }
  return params; // Return unchanged if condition is not met
};

// Social login buttons are needed by AuthenticationForms
export const SocialLoginButtonsMaybe = props => {
  const routeConfiguration = useRouteConfiguration();
  const { isLogin, showFacebookLogin, showGoogleLogin, from } = props;
  const showSocialLogins = showFacebookLogin || showGoogleLogin;

  const getDefaultRoutes = () => {
    const baseUrl = window.location.origin;

    // Route where the user should be returned after authentication
    // This is used e.g. with EditListingPage and ListingPage
    const fromParam = from ? `from=${encodeURIComponent(from)}` : '';

    // Default route where user is returned after successfull authentication
    const baseDefaultReturn = pathByRouteName('Homepage', routeConfiguration);
    const currentLocale = getDefaultLocale();
    const defaultReturn = getUrlForLocale(baseDefaultReturn, currentLocale);
    const defaultReturnParam = defaultReturn ? `&defaultReturn=${defaultReturn}` : '';

    // Route for confirming user data before creating a new user
    const baseDefaultConfirm = pathByRouteName('ConfirmPage', routeConfiguration);
    const defaultConfirm = getUrlForLocale(baseDefaultConfirm, currentLocale);
    const defaultConfirmParam = defaultConfirm ? `&defaultConfirm=${defaultConfirm}` : '';

    return { baseUrl, fromParam, defaultReturnParam, defaultConfirmParam };
  };

  const authWithFacebook = () => {
    const defaultRoutes = getDefaultRoutes();
    const { baseUrl, fromParam, defaultReturnParam, defaultConfirmParam } = defaultRoutes;
    if (isLogin) {
      markPendingWebAuth({ signInMethod: 'facebook', mode: 'login' });
    }
    window.location.href = `${baseUrl}/api/auth/facebook?${fromParam}${defaultReturnParam}${defaultConfirmParam}`;
  };

  const authWithGoogle = () => {
    const defaultRoutes = getDefaultRoutes();
    const { baseUrl, fromParam, defaultReturnParam, defaultConfirmParam } = defaultRoutes;
    if (isLogin) {
      markPendingWebAuth({ signInMethod: 'google', mode: 'login' });
    }
    window.location.href = `${baseUrl}/api/auth/google?${fromParam}${defaultReturnParam}${defaultConfirmParam}`;
  };

  return showSocialLogins ? (
    <div className={css.idpButtons}>
      <div className={css.socialButtonsOr}>
        <span className={css.socialButtonsOrText}>
          <FormattedMessage id="AuthenticationPage.or" />
        </span>
      </div>

      {showFacebookLogin ? (
        <div className={css.socialButtonWrapper}>
          <SocialLoginButton onClick={() => authWithFacebook()}>
            <span className={css.buttonIcon}>{FacebookLogo}</span>
            {isLogin ? (
              <FormattedMessage id="AuthenticationPage.loginWithFacebook" />
            ) : (
              <FormattedMessage id="AuthenticationPage.signupWithFacebook" />
            )}
          </SocialLoginButton>
        </div>
      ) : null}

      {showGoogleLogin ? (
        <div className={css.socialButtonWrapper}>
          <SocialLoginButton onClick={() => authWithGoogle()}>
            <span className={css.buttonIcon}>{GoogleLogo}</span>
            {isLogin ? (
              <FormattedMessage id="AuthenticationPage.loginWithGoogle" />
            ) : (
              <FormattedMessage id="AuthenticationPage.signupWithGoogle" />
            )}
          </SocialLoginButton>
        </div>
      ) : null}
    </div>
  ) : null;
};

// Tabs for SignupForm and LoginForm
export const AuthenticationForms = props => {
  const {
    isLogin,
    from,
    submitLogin,
    loginError,
    signupError,
    authInProgress,
    submitSignup,
    termsAndConditions,
    userType,
    viewport,
    intl,
  } = props;

  const location = useLocation();
  const verifyState = useSelector(verifyCodeSelector) || {};
  const { isContentVoucher, error } = verifyState;

  const parsedVoucherParams = useMemo(() => parseVoucherParams(location.search), [location.search]);
  const storedVoucherState = readVoucherStorage();
  const resolvedVoucher = resolveVoucherInput({
    queryVoucher: parsedVoucherParams?.voucher,
    storedVoucher: storedVoucherState?.voucher,
  });

  const initialVoucher = resolvedVoucher?.voucher || null;
  const initialVoucherSource = resolvedVoucher?.source || null;
  const shouldAutoVerifyVoucher = Boolean(
    initialVoucher && (initialVoucherSource === 'query' || initialVoucherSource === 'storage')
  );

  useEffect(() => {
    if (parsedVoucherParams?.voucher) {
      writeVoucherStorage(parsedVoucherParams.voucher, 'query');
    }
  }, [parsedVoucherParams?.voucher]);

  const fromState = { state: from ? { from } : null };

  const handleSubmitSignup = useCallback(
    async values => {
      const { fname, lname, voucher: voucherInput, ...rest } = values;
      delete rest.salutation;
      const resolvedVoucherInput = voucherInput !== undefined ? voucherInput : initialVoucher;

      const voucherState = deriveVoucherFields({
        voucherInput: resolvedVoucherInput,
        verifyState,
      });

      const voucherFields = error
        ? buildVoucherFields({ voucher: null })
        : buildVoucherFields({
            voucher: voucherState.voucher,
            couponCode: voucherState.couponCode,
            couponId: voucherState.couponId,
          });

      let params = {
        firstName: fname.trim(),
        lastName: lname.trim(),
        userType,
        // Auth voucher logic aligned with Program Onboarding voucher logic
        voucher: voucherFields.voucher,
        couponCode: voucherFields.couponCode,
        couponId: voucherFields.couponId,
        protectedData: {
          ...voucherFields,
        },
        ...(isContentVoucher &&
          !error && {
            hasSubscribed: true,
            couponCode: voucherFields.couponCode,
            couponId: voucherFields.couponId,
          }),
        ...(error && { voucher: null, couponCode: null, couponId: null }),
        ...rest,
      };

      logVoucherDebug('Auth voucher state before submit', {
        voucherInput: resolvedVoucherInput,
        voucherState,
        voucherFields,
        verifyState,
      });
      logVoucherDebug('Auth signup payload', params);

      // Use helper function to transform params
      params = transformUserType(params);

      const response = await submitSignup(params);
      logVoucherDebug('Auth signup response', response);
    },
    [userType, submitSignup, initialVoucher, verifyState, isContentVoucher, error]
  );

  const loginErrorMessage = (
    <div className={css.error}>
      <FormattedMessage id="AuthenticationPage.loginFailed" />
    </div>
  );

  const signupErrorMessage = (
    <div className={css.error}>
      {isSignupEmailTakenError(signupError) ? (
        <FormattedMessage id="AuthenticationPage.signupFailedEmailAlreadyTaken" />
      ) : (
        <FormattedMessage id="AuthenticationPage.signupFailed" />
      )}
    </div>
  );

  const submitErrorMessage = useMemo(() => {
    if (!signupError) return null;
    const firstApiError = Array.isArray(signupError?.apiErrors) ? signupError.apiErrors[0] : null;
    return (
      firstApiError?.meta?.stripeMessage ||
      firstApiError?.detail ||
      firstApiError?.title ||
      signupError?.statusText ||
      signupError?.message ||
      null
    );
  }, [signupError]);

  // eslint-disable-next-line no-confusing-arrow
  const errorMessage = (error, message) => (error ? message : null);
  const loginOrSignupError = isLogin
    ? errorMessage(loginError, loginErrorMessage)
    : errorMessage(signupError && !submitErrorMessage, signupErrorMessage);
  const signupSubmissionError = submitErrorMessage ? (
    <div className={css.error}>{submitErrorMessage}</div>
  ) : null;

  return (
    <div className={css.content}>
      <div className={css.authForm}>
        {loginOrSignupError}
        {signupSubmissionError}

        {isLogin ? (
          <LoginForm className={css.loginForm} onSubmit={submitLogin} inProgress={authInProgress} />
        ) : (
          <SignupForm
            userType={userType}
            className={css.signupForm}
            onSubmit={handleSubmitSignup}
            inProgress={authInProgress}
            termsAndConditions={termsAndConditions}
            viewport={viewport}
            from={from}
            initialValues={initialVoucher ? { voucher: initialVoucher } : undefined}
            initialVoucherSource={initialVoucherSource}
            autoVerifyVoucher={shouldAutoVerifyVoucher}
          />
        )}
      </div>
    </div>
  );
};

// Form for confirming information from IdP (e.g. Facebook)
// This is shown before new user is created to Marketplace API
const ConfirmIdProviderInfoForm = props => {
  const { authInfo, authInProgress, confirmError, submitSingupWithIdp, termsAndConditions } = props;
  const idp = authInfo ? authInfo.idpId.replace(/^./, str => str.toUpperCase()) : null;

  const handleSubmitConfirm = values => {
    const { idpToken, email, firstName, lastName, idpId } = authInfo;
    const { email: newEmail, firstName: newFirstName, lastName: newLastName, ...rest } = values;

    // Pass email, fistName or lastName to Marketplace API only if user has edited them
    // sand they can't be fetched directly from idp provider (e.g. Facebook)

    const authParams = {
      ...(newEmail !== email && { email: newEmail }),
      ...(newFirstName !== firstName && { firstName: newFirstName }),
      ...(newLastName !== lastName && { lastName: newLastName }),
    };

    // If the confirm form has any additional values, pass them forward as user's protected data
    const protectedData = !isEmpty(rest) ? { ...rest } : null;

    submitSingupWithIdp({
      idpToken,
      idpId,
      ...authParams,
      ...(!!protectedData && { protectedData }),
    });
  };

  const confirmErrorMessage = confirmError ? (
    <div className={css.error}>
      {isSignupEmailTakenError(confirmError) ? (
        <FormattedMessage id="AuthenticationPage.signupFailedEmailAlreadyTaken" />
      ) : (
        <FormattedMessage id="AuthenticationPage.signupFailed" />
      )}
    </div>
  ) : null;

  return (
    <div className={css.content}>
      <Heading as="h1" rootClassName={css.signupWithIdpTitle}>
        <FormattedMessage id="AuthenticationPage.confirmSignupWithIdpTitle" values={{ idp }} />
      </Heading>

      <p className={css.confirmInfoText}>
        <FormattedMessage id="AuthenticationPage.confirmSignupInfoText" />
      </p>
      <div className={css.authForm}>
        {confirmErrorMessage}
        <ConfirmSignupForm
          className={css.form}
          onSubmit={handleSubmitConfirm}
          inProgress={authInProgress}
          termsAndConditions={termsAndConditions}
          authInfo={authInfo}
          idp={idp}
        />
      </div>
    </div>
  );
};

export const AuthenticationOrConfirmInfoForm = props => {
  const {
    tab,
    authInfo,
    from,
    showFacebookLogin,
    showGoogleLogin,
    submitLogin,
    submitSignup,
    submitSingupWithIdp,
    authInProgress,
    loginError,
    signupError,
    confirmError,
    termsAndConditions,
    userType,
    location,
    viewport,
    intl,
  } = props;

  const isConfirm = tab === 'confirm';
  const isLogin = tab === 'login';

  return isConfirm ? (
    <ConfirmIdProviderInfoForm
      authInfo={authInfo}
      submitSingupWithIdp={submitSingupWithIdp}
      authInProgress={authInProgress}
      confirmError={confirmError}
      termsAndConditions={termsAndConditions}
    />
  ) : (
    <AuthenticationForms
      isLogin={isLogin}
      showFacebookLogin={showFacebookLogin}
      showGoogleLogin={showGoogleLogin}
      from={from}
      location={location}
      loginError={loginError}
      signupError={signupError}
      submitLogin={submitLogin}
      authInProgress={authInProgress}
      submitSignup={submitSignup}
      termsAndConditions={termsAndConditions}
      userType={userType}
      viewport={viewport}
      intl={intl}
    ></AuthenticationForms>
  );
};

const getAuthInfoFromCookies = () => {
  return Cookies.get('st-authinfo')
    ? JSON.parse(Cookies.get('st-authinfo').replace('j:', ''))
    : null;
};
const getAuthErrorFromCookies = () => {
  return Cookies.get('st-autherror')
    ? JSON.parse(Cookies.get('st-autherror').replace('j:', ''))
    : null;
};

export const AuthenticationPageComponent = props => {
  const [tosModalOpen, setTosModalOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [authInfo, setAuthInfo] = useState(getAuthInfoFromCookies());
  const [authError, setAuthError] = useState(getAuthErrorFromCookies());
  const config = useConfiguration();

  useEffect(() => {
    let isMounted = true;
    if (typeof window !== 'undefined') {
      const handleScroll = () => {
        if (isMounted) {
          setScroll(window.scrollY > 0);
        }
      };
      window.addEventListener('scroll', handleScroll);
      return () => {
        isMounted = false; // This is the cleanup for event listener
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  const [scroll, setScroll] = useState(false);
  const isHeaderSticky = scroll ? css.sticky : '';
  useEffect(() => {
    // Remove the autherror cookie once the content is saved to state
    // because we don't want to show the error message e.g. after page refresh
    if (authError) {
      Cookies.remove('st-autherror');
    }
  }, []);

  // On mobile, it's better to scroll to top.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [tosModalOpen, privacyModalOpen]);

  const {
    authInProgress,
    currentUser,
    intl,
    isAuthenticated,
    location,
    loginError,
    scrollingDisabled,
    signupError,
    submitLogin,
    submitSignup,
    onCreateContactInSendgrid,
    confirmError,
    submitSingupWithIdp,
    tab,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    onResendVerificationEmail,
    onManageDisableScrolling,
    params,
    viewport,
  } = props;

  const authContent = isLocaleGreek() ? authGreekContent : authEnglishContent;

  // History API has potentially state tied to this route
  // We have used that state to store previous URL ("from"),
  // so that use can be redirected back to that page after authentication.
  const locationFrom = location.state?.from || null;
  const authinfoFrom = authInfo?.from || null;
  const searchParams = new URLSearchParams(location.search);
  const redirect = searchParams.get('redirect') || null;
  const from = sanitizeInternalReturnPath(redirect || locationFrom || authinfoFrom);
  const showExpiredMobileLinkMessage = searchParams.get('authMessage') === 'mobile_link_expired';

  const user = ensureCurrentUser(currentUser);
  const currentUserLoaded = !!user.id;
  const isLogin = tab === 'login';

  // We only want to show the email verification dialog in the signup
  // tab if the user isn't being redirected somewhere else
  // (i.e. `from` is present). We must also check the `emailVerified`
  // flag only when the current user is fully loaded.
  const showEmailVerification = !isLogin && currentUserLoaded && !user.attributes.emailVerified;
  const hasCompleteQuestions = user.attributes?.profile?.privateData?.completeQuestions;

  const isCustomer = isIndividualUser(currentUser);

  // Already authenticated, redirect away from auth page
  if (isAuthenticated) {
    if (currentUser?.attributes?.email === config.investorAccount) {
      switchAppLanguage({
        locale: 'en',
        pathname: '/resources',
      });
    }
    if (from) {
      return <Redirect to={from} />;
    }

    if (currentUserLoaded && !showEmailVerification) {
      return <NamedRedirect name="Homepage" />;
    }
  }

  const resendErrorTranslationId = isTooManyEmailVerificationRequestsError(
    sendVerificationEmailError
  )
    ? 'AuthenticationPage.resendFailedTooManyRequests'
    : 'AuthenticationPage.resendFailed';
  const resendErrorMessage = sendVerificationEmailError ? (
    <p className={css.error}>
      <FormattedMessage id={resendErrorTranslationId} />
    </p>
  ) : null;

  const marketplaceName = config.marketplaceName;
  const schemaTitle = isLogin
    ? intl.formatMessage({ id: 'AuthenticationPage.schemaTitleLogin' }, { marketplaceName })
    : intl.formatMessage({ id: 'AuthenticationPage.schemaTitleSignup' }, { marketplaceName });
  const schemaDescription = isLogin
    ? intl.formatMessage({ id: 'AuthenticationPage.schemaDescriptionLogin' }, { marketplaceName })
    : intl.formatMessage({ id: 'AuthenticationPage.schemaDescriptionSignup' }, { marketplaceName });
  const isDoctorSignupRoute = tab === 'signup' && params?.type === USER_TYPE.DOCTOR;
  const doctorSchemaTitle = intl.formatMessage(
    { id: 'AuthenticationPage.schemaTitleSignupDoctor' },
    { marketplaceName }
  );
  const doctorSchemaDescription = intl.formatMessage(
    { id: 'AuthenticationPage.schemaDescriptionSignupDoctor' },
    { marketplaceName }
  );
  const pageTitle = isDoctorSignupRoute ? doctorSchemaTitle : schemaTitle;
  const pageDescription = isDoctorSignupRoute ? doctorSchemaDescription : schemaDescription;

  const topbarClasses = classNames({
    [css.hideOnMobile]: showEmailVerification,
  });

  return (
    <Page
      title={pageTitle}
      description={pageDescription}
      scrollingDisabled={scrollingDisabled}
      noIndex={!isLogin}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: pageTitle,
        description: pageDescription,
      }}
    >
      <LayoutSingleColumn
        mainColumnClassName={css.layoutWrapperMain}
        topbar={<TopbarContainer isFullWidthHeader={true} className={topbarClasses} />}
        footer={<Footer />}
      >
        {/* {!isAuthenticated && !isLogin ? (
          <LinkTabNavHorizontal
            authPageUserTabs={true}
            tabRootClassName={css.tab}
            tabs={tabs}
            skin="dark"
          />
        ) : null} */}

        {showEmailVerification ? (
          <EmailVerificationInfo
            name={user.attributes.profile.firstName}
            isIndividual={isCustomer}
            email={<span className={css.email}>{user.attributes.email}</span>}
            onResendVerificationEmail={onResendVerificationEmail}
            resendErrorMessage={resendErrorMessage}
            sendVerificationEmailInProgress={sendVerificationEmailInProgress}
          />
        ) : (
          <div
            className={classNames(css.formContent, isLogin ? css.formLoginContent : null)}
            data-testid={isLogin ? 'login-page' : 'signup-page'}
          >
            <div className={css.rightSide} data-testid="auth-form-container">
              {showExpiredMobileLinkMessage ? (
                <div className={css.mobileAuthMessage} role="alert">
                  <FormattedMessage id="AuthenticationPage.mobileLinkExpired" />
                </div>
              ) : null}
              <AuthenticationOrConfirmInfoForm
                tab={tab}
                authInfo={authInfo}
                from={from}
                location={location}
                showFacebookLogin={!!process.env.REACT_APP_FACEBOOK_APP_ID}
                showGoogleLogin={!!process.env.REACT_APP_GOOGLE_CLIENT_ID}
                submitLogin={submitLogin}
                submitSignup={submitSignup}
                onCreateContactInSendgrid={onCreateContactInSendgrid}
                submitSingupWithIdp={submitSingupWithIdp}
                authInProgress={authInProgress}
                loginError={loginError}
                signupError={signupError}
                confirmError={confirmError}
                userType={params?.type}
                viewport={viewport}
                intl={intl}
                termsAndConditions={
                  <TermsAndConditions
                    onOpenTermsOfService={() => setTosModalOpen(true)}
                    onOpenPrivacyPolicy={() => setPrivacyModalOpen(true)}
                    intl={intl}
                  />
                }
              />
            </div>
          </div>
        )}
      </LayoutSingleColumn>
      <Modal
        id="AuthenticationPage.tos"
        isOpen={tosModalOpen}
        onClose={() => setTosModalOpen(false)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.modalContent}>
          <div className={css.modalContentHead}>
            <span className={css.modalIcon}>
              <img src={modalImg} alt="" />
            </span>
            <h2>Connect Untitled to Linear</h2>
            <p>
              Prioritize work based on customer needs and build a tighter feedback loop with your
              customers.
            </p>
          </div>
          <div className={css.modalContentBody}>
            <h3>Untitled would like to</h3>
            <ul>
              <li>
                <Icons name="checkCircle" /> Access basic company information and details
              </li>
              <li>
                <Icons name="checkCircle" /> Access and edit bug reports and create new issues
              </li>
              <li>
                <Icons name="checkCircle" /> Change issue status and assignee of issues
              </li>
              <li>
                <Icons name="checkCircle" /> Open and resolve Intercom conversations
              </li>
              <li>
                <Icons name="checkCircle" /> Add or remove users and change user roles
              </li>
            </ul>
          </div>
        </div>
      </Modal>
      <Modal
        id="AuthenticationPage.tos"
        isOpen={tosModalOpen}
        onClose={() => setTosModalOpen(false)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.termsWrapper}>
          <TermsOfServicePage />
        </div>
      </Modal>
      <Modal
        id="AuthenticationPage.privacyPolicy"
        isOpen={privacyModalOpen}
        onClose={() => setPrivacyModalOpen(false)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.privacyWrapper}>
          <PrivacyPolicyPage />
        </div>
      </Modal>
    </Page>
  );
};

AuthenticationPageComponent.defaultProps = {
  currentUser: null,
  loginError: null,
  signupError: null,
  confirmError: null,
  tab: 'signup',
  sendVerificationEmailError: null,
  showSocialLoginsForTests: false,
  privacyAssetsData: null,
  privacyFetchInProgress: false,
  privacyFetchError: null,
  tosAssetsData: null,
  tosFetchInProgress: false,
  tosFetchError: null,
};

AuthenticationPageComponent.propTypes = {
  authInProgress: bool.isRequired,
  currentUser: propTypes.currentUser,
  isAuthenticated: bool.isRequired,
  loginError: propTypes.error,
  scrollingDisabled: bool.isRequired,
  signupError: propTypes.error,
  confirmError: propTypes.error,

  submitLogin: func.isRequired,
  submitSignup: func.isRequired,
  tab: oneOf(['login', 'signup', 'confirm']),

  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  onResendVerificationEmail: func.isRequired,
  onManageDisableScrolling: func.isRequired,

  // to fetch privacy-policy page asset
  // which is shown in modal
  privacyAssetsData: object,
  privacyFetchInProgress: bool,
  privacyFetchError: propTypes.error,

  // to fetch terms-of-service page asset
  // which is shown in modal
  tosAssetsData: object,
  tosFetchInProgress: bool,
  tosFetchError: propTypes.error,

  // from withRouter
  location: shape({ state: object }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { isAuthenticated, loginError, signupError, confirmError } = state.auth;
  const { currentUser, sendVerificationEmailInProgress, sendVerificationEmailError } = state.user;
  const {
    pageAssetsData: privacyAssetsData,
    inProgress: privacyFetchInProgress,
    error: privacyFetchError,
  } = state.hostedAssets || {};
  const { pageAssetsData: tosAssetsData, inProgress: tosFetchInProgress, error: tosFetchError } =
    state.hostedAssets || {};

  return {
    authInProgress: authenticationInProgress(state),
    currentUser,
    isAuthenticated,
    loginError,
    scrollingDisabled: isScrollingDisabled(state),
    signupError,
    confirmError,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    privacyAssetsData,
    privacyFetchInProgress,
    privacyFetchError,
    tosAssetsData,
    tosFetchInProgress,
    tosFetchError,
  };
};

const mapDispatchToProps = dispatch => ({
  submitLogin: ({ email, password }) => dispatch(login(email, password)),
  submitSignup: params => dispatch(signup(params)),
  onCreateContactInSendgrid: params => dispatch(createContactInSendgrid(params)),
  submitSingupWithIdp: params => dispatch(signupWithIdp(params)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const AuthenticationPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl,
  withViewport
)(AuthenticationPageComponent);

export default AuthenticationPage;
